import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import OtpInput from '../components/OtpInput';
import QRCode from '../components/QRCode';
import WorkTypeToggle from '../components/WorkTypeToggle';
import './Login.css';

const EMAIL_DOMAIN = '@mtutravel.com.tr';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // V68.7 — rate limit countdown + remaining attempts
  const [lockedUntil, setLockedUntil] = useState(0); // ms timestamp
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [, setRemainingAttempts] = useState(null);
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();

  // ─── Account Request State ───────────────────────
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqName, setReqName] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqMessage, setReqMessage] = useState('');
  const [reqSuccess, setReqSuccess] = useState('');
  const [reqError, setReqError] = useState('');
  const [reqLoading, setReqLoading] = useState(false);

  // ─── Remember Me State ──────────────────────────────────
  const [rememberMe, setRememberMe] = useState(false);
  // V71.3.1 — çalışma şekli (Ofis / Remote) seçilmeden giriş yapılmaz
  const [workType, setWorkType] = useState('');
  const [workTypeMissing, setWorkTypeMissing] = useState(false);

  // ─── Force Password Change State ───────────────────────
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  // V71 — yedek kod / zorunlu şifre adımı bitene kadar oturum AuthContext'e yazılmaz:
  // user set edilince /login rotası '/'a yönlendirip bu ekranları hiç göstermiyordu.
  // Token yalnız localStorage'a konur (şifre değiştirme isteği için), user bekletilir.
  const [pendingSession, setPendingSession] = useState(null); // { token, user }

  // ─── Email OTP State ─────────────────────────────────────
  const [emailOtpMode, setEmailOtpMode] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  // ─── Mandatory 2FA Setup State ──────────────────────────
  const [mfaSetupMode, setMfaSetupMode] = useState(false);
  const [setupStep, setSetupStep] = useState(0); // 0=not started, 1=QR shown, 2=backup codes
  const [qrSecret, setQrSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);

  // V68.7 — Countdown effect (saniyede bir tick, 0'a inince sayaç ve hata temizlenir)
  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setLockedUntil(0);
        setError('');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const holdSession = (token, userData) => {
    if (!token || !userData) return;
    try { localStorage.setItem('mtu_token', token); } catch {}
    setPendingSession({ token, user: userData });
  };

  const finishLogin = () => {
    if (pendingSession) completeLogin(pendingSession.token, pendingSession.user);
    navigate('/');
  };

  const formatRemaining = (sec) => {
    if (sec >= 60) {
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m} dk ${s} sn`;
    }
    return `${sec} sn`;
  };

  // Step 1: Username + Password
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!workType) {
      setWorkTypeMissing(true);
      setError('Çalışma şeklini seçin: Ofis veya Remote.');
      return;
    }
    setLoading(true);
    try {
      const result = await login(username, password, rememberMe, workType);
      if (result?.mfa_required) {
        setPendingToken(result.pending_token);
        setLoading(false);
        return;
      }
      if (result?.email_otp_required) {
        setPendingToken(result.pending_token);
        setMaskedEmail(result.masked_email || '');
        setEmailOtpMode(true);
        setLoading(false);
        return;
      }
      // V71 — AuthContext.login zorunlu şifre değişikliğinde oturumu yazmadan {must_change_password, token, user} döndürürse
      if (result?.must_change_password && result.token) {
        holdSession(result.token, result.user);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }
      if (result?.mfa_setup_required) {
        setPendingToken(result.pending_token);
        setMfaSetupMode(true);
        try {
          const setupRes = await authAPI.mfaSetupStart(result.pending_token);
          setQrSecret(setupRes.data.secret);
          setOtpauthUrl(setupRes.data.otpauthUrl);
          setSetupStep(1);
        } catch (err) {
          setError(err.response?.data?.error || '2FA kurulumu başlatılamadı');
        }
        setLoading(false);
        return;
      }
      navigate('/');
    } catch (err) {
      // V68.7 — 429 rate limit: countdown başlat
      if (err.response?.status === 429) {
        const sec = Number(err.response.data?.retry_after_sec)
          || Number(err.response.headers?.['retry-after'])
          || Number(err.response.headers?.['ratelimit-reset'])
          || 60;
        setLockedUntil(Date.now() + sec * 1000);
        setError(`Deneme hakkınız doldu. Lütfen ${formatRemaining(sec)} sonra tekrar deneyin.`);
        setRemainingAttempts(0);
      } else {
        // Kalan deneme bilgisini header'dan oku (varsa)
        const remaining = err.response?.headers?.['ratelimit-remaining'];
        if (remaining != null) setRemainingAttempts(Number(remaining));
        const baseMsg = err.response?.data?.error || 'Giriş başarısız. Bilgilerinizi kontrol edin.';
        const tail = (remaining != null && Number(remaining) >= 0) ? ` (Kalan deneme: ${remaining})` : '';
        setError(baseMsg + tail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2b: Email OTP verification
  const handleEmailOtpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.verifyEmailOtp({ pending_token: pendingToken, code: emailOtpCode });
      if (res.data.mfa_setup_required) {
        setEmailOtpMode(false);
        setPendingToken(res.data.pending_token);
        setMfaSetupMode(true);
        const setupRes = await authAPI.mfaSetupStart(res.data.pending_token);
        setQrSecret(setupRes.data.secret);
        setOtpauthUrl(setupRes.data.otpauthUrl);
        setSetupStep(1);
        return;
      }
      if (res.data.must_change_password) {
        holdSession(res.data.token, res.data.user);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }
      completeLogin(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Doğrulama başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: TOTP / Backup code verification (existing MFA)
  const handleMFAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.verifyMFA({ pending_token: pendingToken, code: totpCode });
      if (res.data.must_change_password) {
        // Token saklanır ama oturum henüz açılmaz — şifre ekranı görünür kalsın
        holdSession(res.data.token, res.data.user);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }
      completeLogin(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Doğrulama başarısız. Kodu kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  // MFA Setup: Verify code
  const handleSetupVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.mfaSetupVerify(setupCode, pendingToken);
      setBackupCodes(res.data.backup_codes || []);
      // Zorunlu kurulumda token döner — yedek kodlar gösterilene kadar bekletilir
      if (res.data.token && res.data.user) {
        holdSession(res.data.token, res.data.user);
      }
      // Check if password change is needed after backup codes step
      if (res.data.must_change_password) {
        setMustChangePassword(true);
      }
      setSetupStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Doğrulama başarısız');
    } finally {
      setLoading(false);
    }
  };

  // After showing backup codes — check if password change needed
  const handleSetupComplete = () => {
    if (mustChangePassword) {
      setSetupStep(0);
      setMfaSetupMode(false);
      // mustChangePassword stays true → password change form shows
      return;
    }
    finishLogin();
  };

  // Handle forced password change
  const handleForcePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('Şifreler eşleşmiyor');
      return;
    }
    setLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: password, newPassword });
      finishLogin();
    } catch (err) {
      setError(err.response?.data?.error || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  // ─── Account Request Submit ───────────────────
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setReqError('');
    setReqSuccess('');

    if (!reqEmail.toLowerCase().endsWith(EMAIL_DOMAIN)) {
      setReqError(`Yalnızca ${EMAIL_DOMAIN} uzantılı e-posta adresleri kabul edilir`);
      return;
    }

    setReqLoading(true);
    try {
      const res = await authAPI.requestAccount({ full_name: reqName, email: reqEmail, message: reqMessage });
      setReqSuccess(res.data.message);
      setReqName('');
      setReqEmail('');
      setReqMessage('');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Talep gönderilemedi';
      setReqError(msg);
    } finally {
      setReqLoading(false);
    }
  };

  const goBack = () => {
    setPendingToken(null);
    setTotpCode('');
    setUseBackupCode(false);
    setMfaSetupMode(false);
    setEmailOtpMode(false);
    setEmailOtpCode('');
    setSetupStep(0);
    setSetupCode('');
    setError('');
  };

  const showEmailOtp = emailOtpMode && !!pendingToken && !mustChangePassword;
  const show2FA = !!pendingToken && !mfaSetupMode && !emailOtpMode && !mustChangePassword;
  // V71 — yedek kodlar şifre değişikliği gerekse bile önce gösterilir (buton metni de bunu söylüyor)
  const showSetup = mfaSetupMode && setupStep > 0;
  const showPasswordChange = mustChangePassword && !showSetup;

  // ─── Render ─────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-brand">
          <img src="/mtu-hub-2026-light.png" alt="MTU Agent HUB" className="login-logo-img" />
          <p className="login-welcome">Kurumsal seyahat yönetim platformunuza hoş geldiniz.</p>
        </div>
        <div className="login-footer-left">
          <img src="/mtu-2025-wht.png" alt="MTU Corporate Travel" className="login-footer-logo" />
          <span className="login-footer-text">&copy; {new Date().getFullYear()} MTU Corporate Travel</span>
        </div>
      </div>
      <div className="login-right">

        {/* ─── FORCE PASSWORD CHANGE ── */}
        {showPasswordChange && (
          <form className="login-form" onSubmit={handleForcePasswordChange}>
            <h3 className="login-title">Şifre Belirleme</h3>
            <p className="login-desc">Güvenliğiniz için yeni bir şifre belirlemeniz gerekmektedir. Bu adımı atlayamazsınız.</p>
            {error && <div className="login-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">Yeni Şifre</label>
              <input type="password" className="form-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="En az 6 karakter" required minLength={6} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Yeni Şifre (Tekrar)</label>
              <input type="password" className="form-input" value={newPasswordConfirm} onChange={e => setNewPasswordConfirm(e.target.value)} placeholder="Şifrenizi tekrar girin" required minLength={6} />
              {newPasswordConfirm && newPassword !== newPasswordConfirm && (
                <span className="login-field-error">Şifreler eşleşmiyor</span>
              )}
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={loading || newPassword.length < 6 || newPassword !== newPasswordConfirm}>
              {loading ? 'Kaydediliyor...' : 'Şifremi Belirle'}
            </button>
          </form>
        )}

        {/* ─── EMAIL OTP VERIFY ── */}
        {showEmailOtp && (
          <form className="login-form" onSubmit={handleEmailOtpVerify}>
            <h3 className="login-title">E-posta Doğrulama</h3>
            <p className="login-desc">
              <strong>{maskedEmail}</strong> adresine 6 haneli doğrulama kodu gönderildi.
            </p>
            {error && <div className="login-error">{error}</div>}
            <div className="form-group">
              <label className="form-label" style={{ textAlign: 'center', display: 'block', marginBottom: 12 }}>Doğrulama Kodu</label>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <OtpInput value={emailOtpCode} onChange={setEmailOtpCode} length={6} autoFocus />
              </div>
              <p className="login-hint">E-postanıza gelen 6 haneli kodu girin</p>
              <button type="button" onClick={goBack} className="login-link-btn muted">
                ← Geri dön
              </button>
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={loading || emailOtpCode.length < 6}>
              {loading ? 'Doğrulanıyor...' : 'Doğrula'}
            </button>
          </form>
        )}

        {/* ─── LOGIN FORM ── */}
        {!show2FA && !showSetup && !showRequestForm && !showPasswordChange && !showEmailOtp && (
          <form className="login-form" onSubmit={handleLogin}>
            <h3 className="login-title">Giriş Yap</h3>
            <p className="login-desc">Devam etmek için bilgilerinizi girin</p>
            {error && (
              <div className="login-error">
                {error}
                {secondsLeft > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600 }}>
                    ⏱ Kalan süre: {formatRemaining(secondsLeft)}
                  </div>
                )}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Kullanıcı Adı</label>
              <input type="text" className="form-input" value={username} onChange={e => setUsername(e.target.value)} placeholder="kullanıcı adınız" required autoFocus disabled={secondsLeft > 0} />
            </div>
            <div className="form-group">
              <label className="form-label">Şifre</label>
              <input type="password" className="form-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required disabled={secondsLeft > 0} />
            </div>
            <WorkTypeToggle value={workType} disabled={secondsLeft > 0} invalid={workTypeMissing}
              onChange={v => { setWorkType(v); if (workTypeMissing) { setWorkTypeMissing(false); setError(''); } }} />
            <div className="login-remember">
              <input type="checkbox" id="rememberMe" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
              <label htmlFor="rememberMe">
                Beni hatırla (8 saat)
              </label>
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={loading || secondsLeft > 0}>
              {loading ? 'Doğrulanıyor...' : secondsLeft > 0 ? `Bekleyin (${formatRemaining(secondsLeft)})` : 'Giriş Yap'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" onClick={() => { setShowRequestForm(true); setError(''); }} className="login-link-btn brand">
                Hesabınız yok mu? Erişim talebi gönderin
              </button>
            </div>
          </form>
        )}

        {/* ─── ACCOUNT REQUEST FORM ── */}
        {!show2FA && !showSetup && !showEmailOtp && showRequestForm && (
          <form className="login-form" onSubmit={handleRequestSubmit}>
            <h3 className="login-title">Hesap Talebi</h3>
            <p className="login-desc">Yöneticiye erişim talebi gönderin. Yalnızca <strong>{EMAIL_DOMAIN}</strong> uzantılı e-posta adresleri kabul edilir.</p>
            {reqError && <div className="login-error">{reqError}</div>}
            {reqSuccess && <div className="login-success">{reqSuccess}</div>}
            <div className="form-group">
              <label className="form-label">Ad Soyad</label>
              <input type="text" className="form-input" value={reqName} onChange={e => setReqName(e.target.value)} placeholder="Adınız Soyadınız" required />
            </div>
            <div className="form-group">
              <label className="form-label">E-posta</label>
              <input type="email" className="form-input" value={reqEmail} onChange={e => setReqEmail(e.target.value)} placeholder={`ornek${EMAIL_DOMAIN}`} required />
              {reqEmail && !reqEmail.toLowerCase().endsWith(EMAIL_DOMAIN) && (
                <span className="login-field-error">
                  Yalnızca {EMAIL_DOMAIN} uzantılı adresler kabul edilir
                </span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Mesaj <span style={{ color: 'var(--fg-tertiary)', fontWeight: 400 }}>(opsiyonel)</span></label>
              <textarea className="form-input" value={reqMessage} onChange={e => setReqMessage(e.target.value)} placeholder="Hangi departmanda çalıştığınız, hangi modüllere erişim istediğiniz vb." rows={3} style={{ resize: 'vertical' }} />
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={reqLoading || !reqName || !reqEmail.toLowerCase().endsWith(EMAIL_DOMAIN)}>
              {reqLoading ? 'Gönderiliyor...' : 'Talep Gönder'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button type="button" onClick={() => { setShowRequestForm(false); setReqError(''); setReqSuccess(''); }} className="login-link-btn muted">
                ← Giriş ekranına dön
              </button>
            </div>
          </form>
        )}

        {/* ─── EXISTING MFA VERIFY ── */}
        {show2FA && (
          <form className="login-form" onSubmit={handleMFAVerify}>
            <h3 className="login-title">Doğrulama Kodu</h3>
            <p className="login-desc">Google Authenticator uygulamasındaki 6 haneli kodu veya yedek kodunuzu girin</p>
            {error && <div className="login-error">{error}</div>}
            <div className="form-group">
              {!useBackupCode ? (
                <>
                  <label className="form-label" style={{ textAlign: 'center', display: 'block', marginBottom: 12 }}>6 Haneli Doğrulama Kodu</label>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <OtpInput value={totpCode} onChange={setTotpCode} length={6} autoFocus />
                  </div>
                  <p className="login-hint">Google Authenticator uygulamasındaki kodu girin</p>
                  <button type="button" onClick={() => { setUseBackupCode(true); setTotpCode(''); }} className="login-link-btn brand small">
                    Yedek kod kullan
                  </button>
                </>
              ) : (
                <>
                  <label className="form-label">Yedek Kod</label>
                  <input type="text" className="form-input" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 9))} placeholder="XXXX-XXXX" maxLength={9} autoFocus style={{ fontSize: 20, letterSpacing: 3, textAlign: 'center' }} />
                  <button type="button" onClick={() => { setUseBackupCode(false); setTotpCode(''); }} className="login-link-btn brand small">
                    ← 6 haneli kod gir
                  </button>
                </>
              )}
              <button type="button" onClick={goBack} className="login-link-btn muted">
                ← Geri dön
              </button>
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={loading || totpCode.length < 6}>
              {loading ? 'Doğrulanıyor...' : 'Doğrula'}
            </button>
          </form>
        )}

        {/* ─── MANDATORY 2FA SETUP ── */}
        {showSetup && setupStep === 1 && (
          <form className="login-form" onSubmit={handleSetupVerify} style={{ maxWidth: 420 }}>
            <h3 className="login-title">2FA Kurulumu Zorunlu</h3>
            <p className="login-desc">Güvenliğiniz için iki faktörlü doğrulama kurmanız gerekmektedir. Bu adımı atlayamazsınız.</p>
            {error && <div className="login-error">{error}</div>}

            <div className="login-setup-box">
              <p style={{ fontSize: 13, color: 'var(--fg-secondary)', margin: '0 0 12px' }}>
                1. Google Authenticator uygulamasını açın<br />
                2. Aşağıdaki QR kodu taratın veya anahtarı manuel girin
              </p>
              {otpauthUrl && (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                  <QRCode value={otpauthUrl} size={180} />
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--fg-secondary)', margin: '8px 0 4px' }}>veya manuel olarak girin:</div>
              <div className="login-secret">
                {qrSecret}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ textAlign: 'center', display: 'block', marginBottom: 12 }}>Doğrulama Kodu</label>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <OtpInput value={setupCode} onChange={setSetupCode} length={6} autoFocus />
              </div>
              <p className="login-hint">Authenticator uygulamasından 6 haneli kodu girin</p>
            </div>

            <button type="submit" className="btn btn-primary login-btn" disabled={loading || setupCode.length < 6}>
              {loading ? 'Doğrulanıyor...' : 'Doğrula ve Devam Et'}
            </button>
          </form>
        )}

        {/* ─── BACKUP CODES DISPLAY ── */}
        {showSetup && setupStep === 2 && (
          <div className="login-form" style={{ maxWidth: 420 }}>
            <h3 className="login-title">Yedek Kodlarınız</h3>
            <p className="login-desc">Bu kodları güvenli bir yere kaydedin. Telefonunuzu kaybettiğinizde bu kodlarla giriş yapabilirsiniz.</p>

            <div className="login-backup-box">
              <div className="login-backup-grid">
                {backupCodes.map((code, i) => (
                  <div key={i} className="login-backup-code">
                    {code}
                  </div>
                ))}
              </div>
              <p className="login-backup-note">
                Her yedek kod yalnızca bir kez kullanılabilir
              </p>
            </div>

            <button
              className="btn btn-primary login-btn"
              onClick={handleSetupComplete}
              style={{ marginTop: 8 }}
            >
              {mustChangePassword ? 'Kodları Kaydettim, Şifremi Belirle' : 'Kodları Kaydettim, Devam Et'}
            </button>
          </div>
        )}

        <p className="login-footer">MTU Corporate Travel &copy; {new Date().getFullYear()}</p>
      </div>
    </div>
  );
};

export default Login;
